import { Easing } from "remotion";

/**
 * Design Tokens & Motion Presets according to Remotion Pro Standards
 */
export const theme = {
  colors: {
    bg: "#F8F6FE", // Cool Lavender Canvas
    surface: "#FFFFFF", // Solid Pure White Elevated Card
    surfaceSubtle: "rgba(255, 255, 255, 0.94)",
    border: "#E2E8F0",
    text: "#0F172A",
    muted: "#64748B",
    accent: "#5D3FD3", // Kyu Royal Iris Purple
    accentLight: "#F3E8FF",
    success: "#10B981", // Emerald Green
    successLight: "#ECFDF5",
    gold: "#FFB020", // Solar Gold
    goldLight: "#FEF3C7",
    darkBadge: "#1E124A",
  },
  fonts: {
    display: "'Bebas Neue', sans-serif",
    headline: "'Rajdhani', sans-serif",
    technical: "'Chakra Petch', sans-serif",
  },
  spacing: {
    page: 90,
    gap: 24,
  },
  spring: {
    smooth: { damping: 200 }, // Apple-style no-bounce UI
    snappy: { damping: 20, stiffness: 200 }, // Fast UI clicks
    bouncy: { damping: 10, stiffness: 120 }, // Playful emphasis
  },
  ease: Easing.bezier(0.16, 1, 0.3, 1), // Ease-out-expo
} as const;

/**
 * Neo-Brutalism Light Theme (Anti-AI-Slop Spec)
 * Tangible print-press tactile aesthetic for TikTok / Reels 9:16
 */
export const neoBrutalistLightTheme = {
  colors: {
    canvas: "#F4F0EA", // Warm tactile eggshell / raw paper
    surface: "#FFFFFF", // Solid Pure White Elevated Card
    ink: "#000000",
    border: "#000000",
    canaryYellow: "#FFE500", // Industrial Canary Yellow (zero green tint)
    hazardOrange: "#FF5500", // Vivid Safety Orange
    coralRed: "#FF4365", // Punchy warning coral
    cobaltBlue: "#1B44FF", // Electric Royal Blue
    softCardRed: "#FFF2F4",
  },
  borders: {
    thick: "4px solid #000000",
    medium: "3px solid #000000",
    thin: "2px solid #000000",
  },
  shadows: {
    hardHeavy: "10px 10px 0px #000000",
    hardDefault: "8px 8px 0px #000000",
    hardLight: "4px 4px 0px #000000",
  },
  fonts: {
    display: "'Anton', 'Bebas Neue', sans-serif",
    headline: "'Space Grotesk', sans-serif",
    body: "'Inter', sans-serif",
  },
} as const;

/**
 * Tech Minimalist Showcase Theme (Framer / DTech / SaaS Aesthetic)
 * High-end product showcase with 3D tilt, fanned deck, and cascade catalog grid
 */
export const techMinimalistTheme = {
  colors: {
    canvas: "#F8F9FA", // Pristine warm off-white matte
    surface: "#FFFFFF",
    textDark: "#111827",
    textMuted: "#6B7280",
    textSubtle: "#9CA3AF",
    brandBlue: "#0062FF", // Electric Royal Blue
    brandIndigo: "#5D3FD3", // Royal Iris Purple
    brandEmerald: "#10B981",
    brandOrange: "#F97316",
    borderSubtle: "rgba(0, 0, 0, 0.07)",
    borderHighlight: "rgba(0, 98, 255, 0.15)",
  },
  shadows: {
    softElevation: "0 20px 50px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.03)",
    browserTilt: "0 40px 90px rgba(0, 0, 0, 0.12), 0 8px 24px rgba(0, 0, 0, 0.04)",
    cardFeature: "0 36px 80px rgba(0, 0, 0, 0.09), 0 6px 18px rgba(0, 0, 0, 0.03)",
    fannedHero: "0 36px 90px rgba(0, 98, 255, 0.35)",
    buttonPill: "0 28px 60px rgba(0, 98, 255, 0.35)",
  },
  fonts: {
    display: "'Space Grotesk', -apple-system, sans-serif",
    body: "'Inter', system-ui, sans-serif",
  },
  springs: {
    tilt: { damping: 15, stiffness: 130 },
    snappy: { damping: 16, stiffness: 140 },
    gentle: { damping: 18, stiffness: 120 },
  },
} as const;
